import * as React from 'react';
import { useParams } from 'react-router-dom';
import { IncidentPriority, IncidentStatus, IncidentView } from '../../amorg-common/DTOs/Incident';
import { getComments } from '../../api/commentClientApi';
import { incidentClientApi } from '../../api/incidentClientApi';
import { Button } from '../../designSystem/button/Button';
import { Card } from '../../designSystem/Card';
import { InfoChip } from '../../designSystem/Chip';
import { CollapsiblePanel } from '../../designSystem/collapsible/CollapsiblePanel';
import { colors } from '../../designSystem/colors/color';
import { BaseMessagePanel } from '../../designSystem/compositeComponents/BaseMessagePanel';
import { CommentPanel } from '../../designSystem/compositeComponents/CommentPanel';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { Divider } from '../../designSystem/Divider';
import { FormField } from '../../designSystem/formField/FormField';
import { Icon } from '../../designSystem/icons/Icons';
import { ButtonRadioButton } from '../../designSystem/inputs/ButtonRadioButton';
import { SingleColumnLayout } from '../_layouts/SingleColumnLayout';
import { MainLeftTwoColumnLayout } from '../_layouts/TwoColumnLayouts';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { PageHeader } from '../../designSystem/PageHeader';
import { Tabber } from '../../designSystem/Tabber';
import { ThumbnailImageGallery } from '../../designSystem/ThumbnailImageGallery';
import { P } from '../../designSystem/typography/Body';
import { H2, H3 } from '../../designSystem/typography/Heading';
import { txt } from '../../txt';
import { clientAccessHelper } from '../../utils/clientAccessHelper';
import { toDateTimeString } from '../../utils/date';
import { ProtectedPageContext } from '../_contexts/ProtectedPageContext';
import { MemberPageWrapper } from '../_pageWrappers/MemberPageWrapper';

export function IncidentPage (): JSX.Element
{
    return (
        <MemberPageWrapper selectedMenuItemKey={'incidents'} breadCrumbs={[{ text: 'Indberetninger', url: '/incidents' }, { text: 'Administrér Indberetning', url: '' }]}>
            <IncidentPageContent />
        </MemberPageWrapper>
    );
}

function IncidentPageContent (): JSX.Element
{
    const incidentId = useParams<{ id: string; }>().id;

    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState<
        { status: 'pending'; } |
        { status: 'invalid-data'; } |
        { status: 'ready', incident: IncidentView; }
    >({ status: 'pending' });
    const [tabName, setTabName] = React.useState<'comments' | 'history' | 'messages'>('comments');
    const ctx = React.useContext(ProtectedPageContext);
    const activeUser = ctx.user;
    const hasAccess = clientAccessHelper.canAccessIncidents(activeUser);

    async function reloadData ():Promise<IncidentView | null>
    {
        const nextData = await incidentClientApi.getIncident(incidentId);
        if (!nextData)
            setData({ status: 'invalid-data' });
        else
            setData({ status: 'ready', incident: nextData });
        return nextData;
    }

    React.useEffect(() =>
    {
        const getData = async () =>
        {
            const data = await reloadData();
            if(data){
                if(data.messages.length > 0)
                    setTabName('messages');
                else {
                    // Read comments separately on first load to get the correct comment count
                    const comments = await getComments('Incident', incidentId);
                    if(comments.length > 0)
                        setTabName('comments');
                    else
                        setTabName('history');
                }
            }
            setLoading(false);
        };
        getData();
    }, [incidentId]);

    async function handleChange (newStatus: string, newPriority: string)
    {
        setLoading(true);
        await incidentClientApi.updateIncident(incidentId, newStatus as IncidentStatus, newPriority as IncidentPriority);
        await reloadData();
        setLoading(false);
    }

    console.log(data);

    return (

        <>
            <PageHeader
                narrow
                headerStyle="secondary-gray"
                headerIcon='record_voice_over'
                headerText='Administrér Indberetning' />

        <SingleColumnLayout narrow>
            <Flex flexDirection='column' gap={32}>

                <LoadingPageOverlay loading={loading} />

                {!hasAccess &&
                    <P>
                        Du har ikke adgang til at se indberetninger. Kontakt din administrator hvis du mener dette er en fejl.
                    </P>
                }

                {hasAccess &&

                    <>
                        {data.status === 'invalid-data' &&
                            <P>
                                Indberetningen findes ikke. Den kan være blevet slettet, eller du har måske fulgt et forkert link. Gå tilbage til oversigten og prøv igen.
                            </P>
                        }

                        {data.status === 'ready' &&
                            <>
                                <Flex flexDirection='column' gap={32}>

                                    <MainLeftTwoColumnLayout
                                        main={
                                            <Card variant="outlined" style={{ height: '100%' }}>
                                                <Flex flexDirection="column" gap={16} style={{ height: '100%' }}>

                                                    <div style={{ flexGrow: 1 }}>

                                                        <H2 >{data.incident.title}</H2>

                                                        <P>{data.incident.description}</P>


                                                        {data.incident.solutionDescription &&
                                                            <FormField label='Løsningsforlag'
                                                                inputComponent={
                                                                    <span>{data.incident.solutionDescription}</span>
                                                                } />
                                                        }
                                                    </div>

                                                    <div style={{ flexGrow: 0 }}>


                                                        <Flex justifyContent='flex-end' style={{ fontSize: '14px', color: colors.text.gray.normal }}>
                                                            <span>{data.incident.created.toLocaleString()}</span>
                                                        </Flex>

                                                        <Divider color={colors.card.outlined.default.border} />

                                                        <Flex justifyContent='space-between'>
                                                            <FormField label='Oprettet af'
                                                                inputComponent={
                                                                    <Flex alignItems='center' gap={8}>
                                                                        {data.incident.anonymousSelected
                                                                            ? <>
                                                                                <Icon name="domino_mask" />
                                                                                <span>Anonym</span>
                                                                            </>
                                                                            : <>
                                                                                <Flex flexDirection="column" gap={4}>
                                                                                    <Flex alignItems='center' gap={8}>
                                                                                        <Icon name="interface_user_user" />
                                                                                        <span>{data.incident.name}</span>
                                                                                    </Flex>
                                                                                    <Flex alignItems='center' gap={8}>
                                                                                        <Icon name="interface_actions_mail" />
                                                                                        <span>{data.incident.email}</span>
                                                                                    </Flex>
                                                                                </Flex>
                                                                            </>
                                                                        }
                                                                    </Flex>
                                                                } />


                                                            <FormField label=''
                                                                inputComponent={
                                                                    <Flex alignItems='center' gap={8}>
                                                                        <Icon name='interface_actions_mail' color={
                                                                            data.incident.notificationsSelected ? colors.icon.success : colors.icon.disabled
                                                                        } />

                                                                        <span>{data.incident.notificationsSelected ? 'Må kontaktes' : 'Vil ikke kontaktes'}</span>
                                                                    </Flex>
                                                                } />
                                                        </Flex>
                                                    </div>
                                                </Flex>
                                            </Card>
                                        }
                                        right={
                                            <Card variant="lightGray" style={{ height: '100%' }}>
                                                <Flex flexDirection="column" gap={16}>

                                                    <FormField label='Type'
                                                        inputComponent={
                                                            <span>
                                                                <InfoChip variant="outlined">{txt.incidentTypeText(data.incident.type)}</InfoChip>
                                                            </span>
                                                        } />

                                                    <FormField label='Status'
                                                        inputComponent={
                                                            <Flex flexDirection="column" gap={4}>
                                                                <ButtonRadioButton
                                                                    hideBorder
                                                                    name='status'
                                                                    id='status-new'
                                                                    text={txt.incidentStatusText('new')}
                                                                    value='new'
                                                                    checked={data.incident.status === 'new'}
                                                                    leadingContent={{
                                                                        icon: 'status_error',
                                                                        color: colors.icon.default
                                                                    }}
                                                                    onChange={function (value: string): void
                                                                    {
                                                                        handleChange(value as IncidentStatus, data.incident.priority);
                                                                    }}
                                                                />

                                                                <ButtonRadioButton
                                                                    hideBorder
                                                                    name='status'
                                                                    id='status-in-progress'
                                                                    text={txt.incidentStatusText('in-progress')}
                                                                    value='in-progress'
                                                                    checked={data.incident.status === 'in-progress'}
                                                                    leadingContent={{
                                                                        icon: 'clock',
                                                                        color: colors.icon.default
                                                                    }}
                                                                    onChange={function (value: string): void
                                                                    {
                                                                        handleChange(value as IncidentStatus, data.incident.priority);
                                                                    }}
                                                                />

                                                                <ButtonRadioButton
                                                                    hideBorder
                                                                    name='status'
                                                                    id='status-resolved'
                                                                    text={txt.incidentStatusText('resolved')}
                                                                    value='resolved'
                                                                    checked={data.incident.status === 'resolved'}
                                                                    leadingContent={{
                                                                        icon: 'status_ok',
                                                                        color: colors.icon.default
                                                                    }}
                                                                    onChange={function (value: string): void
                                                                    {
                                                                        handleChange(value as IncidentStatus, data.incident.priority);
                                                                    }}
                                                                />
                                                            </Flex>
                                                        } />

                                                    <FormField label='Prioritering'
                                                        inputComponent={

                                                            <Flex flexDirection="column" gap={4}>

                                                                <Button variant={data.incident.priority === '1 - low' ? 'light' : 'outlined'}
                                                                    
                                                                    text={txt.incidentPriorityText('1 - low')}
                                                                    onClick={() => handleChange(data.incident.status, '1 - low')} />

                                                                <Button variant={data.incident.priority === '2 - normal' ? 'light' : 'outlined'}
                                                                    
                                                                    text={txt.incidentPriorityText('2 - normal')}
                                                                    onClick={() => handleChange(data.incident.status, '2 - normal')} />

                                                                <Button variant={data.incident.priority === '3 - high' ? 'light' : 'outlined'}
                                                                    
                                                                    text={txt.incidentPriorityText('3 - high')}
                                                                    onClick={() => handleChange(data.incident.status, '3 - high')} />

                                                                <Button variant={data.incident.priority === '4 - urgent' ? 'destructive' : 'outlined'}
                                                                    
                                                                    text={txt.incidentPriorityText('4 - urgent')}
                                                                    onClick={() => handleChange(data.incident.status, '4 - urgent')} />

                                                            </Flex>

                                                        } />
                                                </Flex>
                                            </Card>
                                        }
                                    />

                                    <CollapsiblePanel
                                        defaultOpen={data.incident.images.length > 0}
                                        closedOrDefaultHeader={
                                            <Flex gap={8}>
                                                <Icon name="file_image" color={colors.icon.active} />
                                                <H3>Billeder ({data.incident.images.length})</H3>
                                            </Flex>
                                        }>
                                        <ThumbnailImageGallery files={data.incident.images} />
                                    </CollapsiblePanel>

                                    <Flex flexDirection='column' gap={16}>
                                        <Tabber
                                            items={[
                                                { tabName: 'messages', text: `Beskeder (${data.incident?.messages.length || 0})`, disabled: !data.incident.notificationsSelected },
                                                { tabName: 'comments', text: 'Kommentarer ' },
                                                { tabName: 'history', text: 'Historik' },
                                            ]}
                                            variant='text'
                                            inline
                                            selectedTabName={tabName}
                                            onClick={(tabName) => setTabName(tabName as 'comments' | 'history' | 'messages')} />

                                        <Card variant="outlined" style={{ minHeight: '100px' }}>
                                            {tabName === 'messages' &&
                                                <IncidentMessagesTab
                                                    incident={data.incident}
                                                    incidentId={incidentId}
                                                    onReloadData={async () => { await reloadData(); return; }}
                                                    setLoading={setLoading}
                                                />
                                            }

                                            {tabName === 'comments' && activeUser &&
                                                <CommentPanel
                                                    activeUser={activeUser}
                                                    commentReference={{ type: 'Incident', id: data.incident.id }} />
                                            }

                                            {tabName === 'history' &&
                                                <HistoryTab incident={data.incident} />
                                            }
                                        </Card>
                                    </Flex>
                                </Flex>
                            </>
                        }
                    </>
                }
            </Flex>
        </SingleColumnLayout>
        </>
    );
}


function HistoryTab ({incident}: {incident: IncidentView}): JSX.Element
{
    return (
        <Flex flexDirection='column' gap={16} style={{ fontSize: '14px' }}>
            {incident.history.map((h, index) => (
                <Flex key={index} gap={4}>
                    <div style={{ flexGrow: 1 }}>
                        <span style={{ marginLeft: '24px', color: colors.text.gray.normal }}>
                            {h.type === 'CreatedIncident' && 'Indberetning oprettet'}
                            {h.type === 'UpdatedIncident' && `Indberetning opdateret (status: ${txt.incidentStatusText(h.status)}, prioritet: ${txt.incidentPriorityText(h.priority)})`}
                            {h.type === 'SentMessageToIncidentReporter' && `Besked sendt til indberetteren (${h.userName})`}
                            {h.type === 'SentMessageToIncidentResponders' && `Besked sendt til organisationen`}
                            {h.type === 'Unknown' && 'Ukendt hændelse'}
                        </span>

                    </div>
                    <div style={{ flexGrow: 0 }}>
                        {h.userName}
                    </div>
                    <div style={{ flexGrow: 0 }}>
                        {toDateTimeString(h.timestamp)}
                    </div>
                </Flex>
            ))}
        </Flex>
    );
}

type MessagesTabProps = {
    incident: IncidentView,
    incidentId: string,
    onReloadData: () => Promise<void>,
    setLoading: React.Dispatch<React.SetStateAction<boolean>>,
};

function IncidentMessagesTab ({ incident, incidentId, onReloadData, setLoading }: MessagesTabProps): JSX.Element
{
    async function handleSendMessage (message:string): Promise<{errorMessage?:string}>
    {
        const trimmedMessage = message.trim();
        if (trimmedMessage === '')
        {
            return { errorMessage: 'Beskeden skal udfyldes.' };
        }

        setLoading(true);

        const result = await incidentClientApi.sendIncidentReporterMessage(incidentId, trimmedMessage);
        if (!result.isSuccess)
        {
            
            setLoading(false);
            return { errorMessage: 'Der opstod en fejl. Prøv igen eller kontakt support.' };
        }

        await onReloadData();
        setLoading(false);

        return { errorMessage:'' };
    }

    return (
        <Flex flexDirection='column' gap={24}>

            <BaseMessagePanel
                texts={{
                    deleteModalHeading: "Slet beskeden", // Not possible
                    deleteModalConfirmation: "Er du sikker på, at du vil slette beskeden?", // Not possible
                    addButton: "Skriv besked",
                    noComments: "Ingen beskeder sendt",
                    deletedConfirmation: "Beskeden er slettet", // Not possible
                    editModalHeader: "Rediger beskeden", // Not possible
                    editModalLabel: "Besked" // Not possible
                }}
                messages={incident.messages.map(m => {
                    const createdBy = m.sender.type === 'reporter'
                        ? { displayName:'Indberetter', userId:'' }
                        : { displayName: m.sender.name, userId: m.sender.userId };
                    return {
                        id: m.id,
                        created: m.created,
                        content: m.message,
                        createdBy,
                        updated: null,
                        deleted: false,
                    }
                })}
                onCreate={async (message) => handleSendMessage(message) }
                createDisabled={incident.status === 'resolved'}
                createDisabledTooltip={incident.status === 'resolved' ? 'Der kan ikke sendes beskeder til en afsluttet indberetning.' : undefined}
            />
        </Flex>
    );
}